import { CircleCheck, Info, TriangleAlert, type LucideIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * InfoBanner (W-2 Profile KYC): a static, non-live notice with a title, a
 * body and an optional action. Tone picks the icon and accent; the copy
 * carries the meaning, so it reads the same in greyscale.
 */

export type InfoBannerTone = "success" | "info" | "warning";

const TONES: Record<InfoBannerTone, { icon: LucideIcon; border: string; text: string }> = {
  success: { icon: CircleCheck, border: "border-l-status-success", text: "text-status-success" },
  info: { icon: Info, border: "border-l-status-info", text: "text-status-info" },
  warning: { icon: TriangleAlert, border: "border-l-status-warning", text: "text-status-warning" },
};

export interface InfoBannerProps extends Omit<React.ComponentProps<"div">, "title"> {
  tone: InfoBannerTone;
  title: string;
  body?: string;
  action?: React.ReactNode;
}

function InfoBanner({ tone, title, body, action, className, ...props }: InfoBannerProps) {
  const { icon: Icon, border, text } = TONES[tone];
  return (
    <div
      data-slot="info-banner"
      data-tone={tone}
      className={cn(
        "flex items-start gap-3 rounded-lg border border-l-4 bg-card p-4 text-card-foreground",
        border,
        className,
      )}
      {...props}
    >
      <Icon aria-hidden="true" className={cn("mt-0.5 size-5 shrink-0", text)} />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="font-semibold">{title}</p>
        {body ? <p className="text-sm text-muted-foreground">{body}</p> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
    </div>
  );
}

export { InfoBanner };
